import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Stock } from '../types';
import { StockService } from '../services/stockService';
import { DividendService, DividendData } from '../services/dividendService';
import { logger } from '../utils/logger';

interface StockDataContextType {
  allStocks: Stock[]; // 顯示用的股票列表（限制數量）
  fullStocks: Stock[]; // 完整的股票列表（供其他功能使用）
  dividendsData: Map<string, DividendData[]>; // 股利資料 (symbol -> dividend data)
  isLoading: boolean;
  isDividendLoading: boolean;
  lastUpdated: Date | null;
  error: string | null;
  stocksLimit: number; // 顯示的股票數量限制
  totalStocksCount: number; // 總股票數量
  loadStocks: () => Promise<void>;
  refreshStocks: () => Promise<void>;
  loadMoreStocks: () => Promise<void>; // 載入更多股票
  searchStocks: (query: string, limit?: number) => Stock[]; // 從完整列表搜尋
  getStockBySymbol: (symbol: string) => Stock | undefined; // 從完整列表查找
  getDividendData: (symbol: string) => Promise<DividendData[]>; // 獲取股利資料
  getMultipleDividendData: (symbols: string[]) => Promise<Map<string, DividendData[]>>; // 批量獲取股利資料
}

const StockDataContext = createContext<StockDataContextType | undefined>(undefined);

export const useStockData = () => {
  const context = useContext(StockDataContext);
  if (!context) {
    throw new Error('useStockData must be used within a StockDataProvider');
  }
  return context;
};

interface StockDataProviderProps {
  children: React.ReactNode;
  // 記憶體快取有效期（分鐘），預設為 30 分鐘
  // 這可以避免頻繁調用 API，提升效能
  cacheValidityMinutes?: number;
  // 初始顯示的股票數量，預設 50 筆
  initialStockLimit?: number;
}

export const StockDataProvider: React.FC<StockDataProviderProps> = ({ 
  children, 
  cacheValidityMinutes = 30,
  initialStockLimit = 50
}) => {
  const [allStocks, setAllStocks] = useState<Stock[]>([]);
  const [fullStocksList, setFullStocksList] = useState<Stock[]>([]); // 完整清單
  const [dividendsData, setDividendsData] = useState<Map<string, DividendData[]>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [isDividendLoading, setIsDividendLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 使用 ref 來防止重複請求
  const isLoadingRef = useRef(false);
  const autoRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ARCH-02:mounted flag — unmount 後不得再「重新武裝」自動刷新 timer,
  // 否則背景輪詢永不停止(舊 cleanup 只清當下的 timer,callback 續行時會再設新 timer)
  const isMountedRef = useRef(true);
  // ARCH-02:lastUpdated 改以 ref 讀取最新值,避免 closure 捕捉舊值導致提早判定過期
  const lastUpdatedRef = useRef<Date | null>(null);
  React.useEffect(() => {
    lastUpdatedRef.current = lastUpdated;
  }, [lastUpdated]);

  // 核心載入函數，兩個公開方法都會使用
  const fetchStocks = useCallback(async () => {
    const timestamp = new Date().toISOString();
    logger.stockData.info('StockDataContext.fetchStocks() called', { timestamp });
    
    if (isLoadingRef.current) {
      logger.stockData.warn('fetchStocks: Request already in progress, cancelling this request');
      return;
    }
    
    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      logger.stockData.info('Calling StockService.getAllStocks()');
      const stocks = await StockService.getAllStocks();
      
      // 儲存完整清單
      setFullStocksList(stocks);

      // 只顯示前 N 筆股票，提升效能
      const limitedStocks = stocks.slice(0, initialStockLimit);
      setAllStocks(limitedStocks);
      
      setLastUpdated(new Date());
      setError(null);
      
      logger.stockData.info('StockDataContext.fetchStocks() successfully loaded stocks', {
        totalStocks: stocks.length,
        displayedStocks: limitedStocks.length
      });
      
      return stocks;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '載入股票資料失敗';
      logger.stockData.error('StockDataContext.fetchStocks() failed', err);
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [initialStockLimit]);

  // 設定自動刷新計時器 - 定期清理過期快取(ARCH-02:含 mounted 防護與 ref 讀取)
  const setupAutoRefresh = useCallback(() => {
    if (autoRefreshTimerRef.current) {
      clearTimeout(autoRefreshTimerRef.current);
    }

    autoRefreshTimerRef.current = setTimeout(async () => {
      // unmount 後不再排程(舊版會在此重新武裝 timer 造成永久背景請求)
      if (!isMountedRef.current) return;
      logger.stockData.info('Checking cache validity for auto refresh');
      try {
        const currentLastUpdated = lastUpdatedRef.current;
        if (currentLastUpdated) {
          const timeDiff = Date.now() - currentLastUpdated.getTime();
          const maxAge = cacheValidityMinutes * 60 * 1000;
          if (timeDiff >= maxAge) {
            logger.stockData.info('Cache expired, refreshing stock data');
            await fetchStocks();
          }
        }
        setupAutoRefresh();
      } catch (error) {
        logger.stockData.error('Auto refresh check failed', error);
        if (isMountedRef.current) {
          setupAutoRefresh();
        }
      }
    }, cacheValidityMinutes * 60 * 1000);
  }, [cacheValidityMinutes, fetchStocks]);

  // 載入股票資料（有快取機制）
  const loadStocks = useCallback(async () => {
    logger.stockData.info('StockDataContext.loadStocks() called', {
      currentStocksCount: allStocks.length,
      isLoading: isLoadingRef.current,
      lastUpdated: lastUpdated?.toISOString()
    });
    
    // 防止重複請求
    if (isLoadingRef.current) {
      logger.stockData.warn('loadStocks: Stock data loading in progress, skipping duplicate request');
      return;
    }

    // 如果記憶體快取資料存在且在有效期內，直接返回
    if (allStocks.length > 0 && lastUpdated) {
      const timeDiff = Date.now() - lastUpdated.getTime();
      const maxAge = cacheValidityMinutes * 60 * 1000; // 轉換為毫秒
      logger.stockData.info('Memory cache check', { timeDiff, maxAge });
      if (timeDiff < maxAge) {
        logger.stockData.info('Using cached stock data to avoid duplicate API calls');
        return;
      }
    }

    try {
      logger.stockData.info('loadStocks: Calling fetchStocks()...');
      await fetchStocks();
      logger.stockData.info('loadStocks: Calling setupAutoRefresh()...');
      setupAutoRefresh(); // 設定自動刷新
    } catch (err) {
      logger.stockData.warn('loadStocks: Error occurred, checking if should set empty array');
      // 如果 API 失敗且沒有現有資料，使用空陣列
      if (allStocks.length === 0) {
        logger.stockData.info('loadStocks: Setting empty array');
        setAllStocks([]);
      }
    }
    logger.stockData.info('StockDataContext.loadStocks() completed');
  }, [allStocks.length, lastUpdated, cacheValidityMinutes, fetchStocks, setupAutoRefresh]);

  // 強制刷新股票資料（無快取機制）
  const refreshStocks = useCallback(async () => {
    logger.stockData.info('StockDataContext.refreshStocks() called');
    
    try {
      logger.stockData.info('refreshStocks: Calling fetchStocks()...');
      await fetchStocks();
      logger.stockData.info('refreshStocks: Calling setupAutoRefresh()...');
      setupAutoRefresh(); // 重設自動刷新
    } catch (err) {
      logger.stockData.warn('refreshStocks: Error occurred, handled in fetchStocks');
    }
    logger.stockData.info('StockDataContext.refreshStocks() completed');
  }, [fetchStocks, setupAutoRefresh]);

  // 載入更多股票功能（簡化版）
  const loadMoreStocks = useCallback(async () => {
    logger.stockData.info('StockDataContext.loadMoreStocks() called', {
      displayedStocks: allStocks.length,
      totalStocks: fullStocksList.length
    });
    
    // 簡化實作：直接說明已是精選的前 50 筆
    logger.stockData.info('System optimization: Already displaying curated top stocks, no need to load more', {
      displayedCount: allStocks.length
    });
  }, [allStocks.length, fullStocksList.length]);

  // 搜尋股票（從完整列表搜尋）
  const searchStocks = useCallback((query: string, limit: number = 50): Stock[] => {
    if (!query.trim()) {
      return fullStocksList.slice(0, limit);
    }

    const searchTerm = query.toLowerCase();
    return fullStocksList
      .filter(stock => 
        stock.symbol.toLowerCase().includes(searchTerm) ||
        stock.name.toLowerCase().includes(searchTerm)
      )
      .slice(0, limit);
  }, [fullStocksList]);

  // 根據股票代號取得股票資料（從完整列表查找）
  const getStockBySymbol = useCallback((symbol: string): Stock | undefined => {
    return fullStocksList.find(stock => stock.symbol === symbol);
  }, [fullStocksList]);

  // 獲取單一股票的股利資料
  const getDividendData = useCallback(async (symbol: string): Promise<DividendData[]> => {
    logger.dividend.info(`Getting dividend data for symbol: ${symbol}`);
    
    try {
      // 先檢查快取中是否有資料
      if (dividendsData.has(symbol)) {
        logger.dividend.info(`Using cached dividend data for ${symbol}`);
        return dividendsData.get(symbol)!;
      }
      
      setIsDividendLoading(true);
      
      // 從 API 獲取資料
      const data = await DividendService.getStockDividend(symbol);
      
      // 更新快取(ARCH-09:先複製再 set — prev.set() 會就地變異目前的 state Map)
      setDividendsData(prev => {
        const updated = new Map(prev);
        updated.set(symbol, data);
        return updated;
      });
      
      logger.dividend.info(`Successfully loaded dividend data for ${symbol}`, { recordCount: data.length });
      return data;
    } catch (error) {
      logger.dividend.error(`Failed to get dividend data for ${symbol}`, error);
      throw error;
    } finally {
      setIsDividendLoading(false);
    }
  }, [dividendsData]);

  // 批量獲取多個股票的股利資料
  const getMultipleDividendData = useCallback(async (symbols: string[]): Promise<Map<string, DividendData[]>> => {
    logger.dividend.info(`Getting dividend data for multiple symbols`, { symbols, count: symbols.length });
    
    try {
      // 先檢查快取中已有的資料
      const result = new Map<string, DividendData[]>();
      const symbolsToFetch: string[] = [];
      
      for (const symbol of symbols) {
        if (dividendsData.has(symbol)) {
          result.set(symbol, dividendsData.get(symbol)!);
        } else {
          symbolsToFetch.push(symbol);
        }
      }
      
      if (symbolsToFetch.length === 0) {
        logger.dividend.info('All dividend data found in cache');
        return result;
      }
      
      setIsDividendLoading(true);
      
      // 批量獲取缺失的資料
      const newData = await DividendService.getMultipleStockDividends(symbolsToFetch);
      
      // 更新快取和結果
      setDividendsData(prev => {
        const updated = new Map(prev);
        Object.entries(newData).forEach(([symbol, data]) => {
          updated.set(symbol, data);
          result.set(symbol, data);
        });
        return updated;
      });
      
      logger.dividend.info(`Successfully loaded dividend data for multiple symbols`, { 
        fetchedCount: symbolsToFetch.length,
        totalCount: symbols.length 
      });
      
      return result;
    } catch (error) {
      logger.dividend.error('Failed to get multiple dividend data', error);
      throw error;
    } finally {
      setIsDividendLoading(false);
    }
  }, [dividendsData]);

  // 初始化時自動載入股票資料
  // ARCH-02:移除 isInitializedRef 提前 return — StrictMode(dev)會 mount→cleanup→mount,
  // 第一次設定 ref 後 cleanup 只清 timer,第二次因 ref 已 true 提前 return,
  // 初始化路徑在 dev 模式永遠不會執行
  React.useEffect(() => {
    isMountedRef.current = true;

    // 延遲一小段時間再載入，確保其他 context 都已就緒
    const initTimer = setTimeout(() => {
      if (isMountedRef.current) {
        loadStocks();
      }
    }, 100);

    return () => {
      isMountedRef.current = false;
      clearTimeout(initTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 清理計時器
  React.useEffect(() => {
    return () => {
      if (autoRefreshTimerRef.current) {
        clearTimeout(autoRefreshTimerRef.current);
      }
    };
  }, []);

  // ARCH-05:context value 以 useMemo 提供,避免每次 render 重建物件
  // 造成所有消費者不必要的重渲染
  const value: StockDataContextType = React.useMemo(() => ({
    allStocks, // 限制顯示的股票（供股票報價頁面使用）
    fullStocks: fullStocksList, // 完整股票列表（供其他功能使用）
    dividendsData, // 股利資料快取
    isLoading,
    isDividendLoading,
    lastUpdated,
    error,
    stocksLimit: initialStockLimit,
    totalStocksCount: fullStocksList.length,
    loadStocks,
    refreshStocks,
    loadMoreStocks,
    searchStocks,
    getStockBySymbol,
    getDividendData,
    getMultipleDividendData,
  }), [
    allStocks, fullStocksList, dividendsData, isLoading, isDividendLoading,
    lastUpdated, error, initialStockLimit, loadStocks, refreshStocks,
    loadMoreStocks, searchStocks, getStockBySymbol, getDividendData,
    getMultipleDividendData,
  ]);

  return (
    <StockDataContext.Provider value={value}>
      {children}
    </StockDataContext.Provider>
  );
};

// 輔助 Hook - 確保股票資料已載入
export const useEnsureStockData = () => {
  const { allStocks, loadStocks, isLoading } = useStockData();
  
  React.useEffect(() => {
    if (allStocks.length === 0 && !isLoading) {
      loadStocks();
    }
  }, [allStocks.length, isLoading, loadStocks]);
  
  return { allStocks, isLoading };
};
